import { createApp, ref, type App, type Ref } from "vue";
import LoginComponent from "../components/Login.vue";

interface UseLoginOptions {
  title?: string;
  subTitle?: string;
  tips?: string;
  phone?: string;
  showCancel?: boolean;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: (data: { phone: string; code: string }) => Promise<unknown>;
  onCancel?: () => void;
  onSuccess?: () => void;
}

interface LoginResult {
  action: "success" | "cancel";
  data?: { phone: string; code: string };
}

interface LoginInstance {
  app: App;
  container: HTMLDivElement;
  resolve: (value: LoginResult) => void;
}

interface UseLoginReturn {
  showLogin: (overrideOptions?: Partial<UseLoginOptions>) => Promise<LoginResult>;
  hideLogin: () => void;
  forceHide: () => void;
  isVisible: Readonly<Ref<boolean>>;
}

const defaultOptions: UseLoginOptions = {
  title: "登录",
  showCancel: true,
  confirmText: "登录",
  cancelText: "取消"
};

// 全局单例管理器
class GlobalLoginManager {
  private static _instance: GlobalLoginManager | null = null;
  private loginInstance: LoginInstance | null = null;
  private currentPromise: Promise<LoginResult> | null = null;
  private isResolved = false;
  public isVisible = ref(false);

  static getInstance(): GlobalLoginManager {
    if (!GlobalLoginManager._instance) {
      GlobalLoginManager._instance = new GlobalLoginManager();
    }
    return GlobalLoginManager._instance;
  }

  private constructor() {}

  // 立即同步清理函数 - 用于强制销毁实例
  private cleanupSync() {
    if (this.loginInstance) {
      try {
        this.loginInstance.app.unmount();
        if (document.body.contains(this.loginInstance.container)) {
          document.body.removeChild(this.loginInstance.container);
        }
      } catch (error) {
        console.warn("清理登录实例时出错:", error);
      }
      this.loginInstance = null;
    }
    this.currentPromise = null;
    this.isResolved = false;
    this.isVisible.value = false;
  }

  // 异步清理函数 - 用于正常关闭场景
  private cleanup() {
    if (this.loginInstance) {
      setTimeout(() => {
        if (this.loginInstance) {
          this.loginInstance.app.unmount();
          if (document.body.contains(this.loginInstance.container)) {
            document.body.removeChild(this.loginInstance.container);
          }
          this.loginInstance = null;
        }
        this.currentPromise = null;
      }, 300); // 等待动画完成
    }
  }

  // 处理用户操作的统一函数
  private handleUserAction(action: "success" | "cancel", data?: { phone: string; code: string }) {
    if (this.isResolved) return;
    this.isResolved = true;

    this.isVisible.value = false;

    // 延迟 resolve 和清理
    setTimeout(() => {
      if (this.loginInstance) {
        this.loginInstance.resolve({ action, data });
        this.cleanup();
      }
    }, 300);
  }

  // 内部显示登录框的实现
  private showLoginInternal(finalOptions: UseLoginOptions): Promise<LoginResult> {
    return new Promise((resolve) => {
      // 创建容器元素
      const container = document.createElement("div");
      document.body.appendChild(container);

      this.isResolved = false;
      this.isVisible.value = true;

      // 增强的配置选项，包含内部处理逻辑
      const enhancedOptions = {
        ...finalOptions,
        onConfirm: finalOptions.onConfirm,
        onCancel: () => {
          finalOptions.onCancel?.();
          this.handleUserAction("cancel");
        },
        onSuccess: () => {
          finalOptions.onSuccess?.();
          this.handleUserAction("success");
        }
      };
      console.log("enhancedOptions", enhancedOptions);

      const app = createApp(LoginComponent, enhancedOptions);
      this.loginInstance = {
        app,
        container,
        resolve
      };

      // 挂载应用
      app.mount(container);
    });
  }

  // 显示登录框 - 纯覆盖策略
  showLogin(userOptions: UseLoginOptions, overrideOptions: Partial<UseLoginOptions> = {}): Promise<LoginResult> {
    const finalOptions = { ...defaultOptions, ...userOptions, ...overrideOptions };
    if (this.loginInstance && !this.isResolved) {
      this.loginInstance.resolve({ action: "cancel" });
      this.cleanupSync();
    }

    this.currentPromise = this.showLoginInternal(finalOptions);
    return this.currentPromise;
  }

  // 隐藏登录框
  hideLogin() {
    if (this.loginInstance && !this.isResolved) {
      this.handleUserAction("cancel");
    }
  }

  // 强制隐藏登录框
  forceHide() {
    if (this.loginInstance && !this.isResolved) {
      this.handleUserAction("cancel");
    }
  }
}

function getGlobalManager(): GlobalLoginManager {
  return GlobalLoginManager.getInstance();
}

export function useLogin(userOptions: UseLoginOptions = {}): UseLoginReturn {
  const manager = getGlobalManager();

  const showLogin = (overrideOptions: Partial<UseLoginOptions> = {}): Promise<LoginResult> => {
    return manager.showLogin(userOptions, overrideOptions);
  };

  const hideLogin = () => {
    manager.hideLogin();
  };

  const forceHide = () => {
    manager.forceHide();
  };

  return {
    showLogin,
    hideLogin,
    forceHide,
    isVisible: manager.isVisible as Readonly<Ref<boolean>>
  };
}

export type { UseLoginOptions, LoginResult, UseLoginReturn };
